import { Ng2StateDeclaration } from '@uirouter/angular';
import { Transition } from '@uirouter/core';
import { HttpClient } from '@angular/common/http';
import * as _ from 'lodash';
import { ForceSameUser } from '../../config/core.resolve.same-user';
import { StateService } from '@uirouter/angular';
import { doGetPromise } from '../../config/core.httpclient-utils';
import { SiteConfigService } from '../../services/core.site-config.service';

import { NewUserComponent } from './new-user/new-user.component';
import { ChangePasswordComponent } from './changepassword/changepassword.component';
import { ChangeEmailRequestComponent } from './changeemail/request/changeemail.request.component';
import { ChangeEmailConfirmComponent } from './changeemail/confirm/changeemail.confirm.component';
import { LoginComponent } from './login/login.component';
import { LostPasswordViewComponent } from './lostpassword/lostpassword.component';
import { RegisterViewComponent } from './userRegistration/register/register.component';
import { ConfirmViewComponent } from './userRegistration/confirm/confirm.component';
import { UserRegistrationRequestComponent } from './userRegistration/request/user.registration.request.component';
import { ResetPasswordViewComponent } from './resetpassword/resetpassword.component';
import { UserHomeViewComponent } from './home/user.home.component';
import { UserMyViewComponent } from './my/user.my.component';
import { UserNotificationsViewComponent } from './notifications/user.notifications.component';
import { UserProfileEditComponent } from './edit-profile/user.edit-profile.component';
import { SshKeysViewComponent } from './ssh-keys/user.ssh-keys.component';
import { MyReportsListComponent } from './myreports/list/myreports.list.component';
import { MyReportsReportComponent } from './myreports/report/myreports.report.component';
import { UserNotebookComponent } from './notebook/user.notebook.component';
import { UserNotebookPageComponent } from './notebook/user.notebook-page.component';

import { UserService } from '../../services/core.user.service';
import { TourService } from '../tour/tour.service';
import { UserRest } from '../../services/core.user.rest.service';
import { MyReportsRest } from './myreports/services/myreports.service';
import { UiService } from '../../services/core.ui.service';
import { StateHelperService } from '../../services/core.ui.state.service';

export const USER_STATES: any[] = [
    {
        name: 'home.user',
        url: 'user',
        abstract: true,
        resolve: [
            {
                token: 'registrationMode',
                deps: ['siteConfig'],
                resolveFn: async (siteConfig: any) => {
                    return _.get(siteConfig, 'registrationMode', 0);
                }
            },
            {
                token: 'templates',
                deps: [],
                resolveFn: async () => {
                    return true;
                }
            }
        ]
    },
    {
        name: 'home.user.welcome',
        url: '/:unixName/welcome',
        resolve: [
            {
                token: 'profile',
                deps: ['user', UserService],
                resolveFn: async (user: any, userService: UserService) => {
                    if (!userService.session.hasUser()) {
                        throw new Error('User not logged in');
                    }
                    const response = await userService.profile.get(userService.session.getUnixName());
                    return response.data;
                }
            }
        ],
        views: {
            'contentarea@home': {
                component: NewUserComponent
            }
        }
    },
    {
        name: 'home.user.changepassword',
        url: '/:unixName/changepassword',
        resolve: [
            {
                token: 'security',
                deps: [UserService],
                resolveFn: async (userService: UserService) => {
                    return userService.session.verify(true);
                }
            },
            {
                token: 'tour',
                deps: [TourService],
                resolveFn: async (tourService: TourService) => {
                    return tourService.setTour('changePasswordCtrl');
                }
            },
            {
                token: 'user',
                deps: [UserService],
                resolveFn: async (userService: UserService) => {
                    return userService.session.getUser();
                }
            },
            {
                token: 'sameUser',
                deps: ['user', '$transition$'],
                resolveFn: async (user: any, transition: any) => {
                    const params = transition.params();
                    return ForceSameUser('home.user.changepassword', user, params, Promise);
                }
            }
        ],
        views: {
            'contentarea@home': {
                component: ChangePasswordComponent
            }
        }
    },
    {
        name: 'home.user.changeemail',
        abstract: true,
        url: '/:unixName/changeemail',
        resolve: [
            {
                token: 'security',
                deps: [UserService],
                resolveFn: async (userService: UserService) => {
                    return userService.session.verify(true);
                }
            },
            {
                token: 'user',
                deps: [UserService],
                resolveFn: async (userService: UserService) => {
                    return userService.session.getUser();
                }
            },
            {
                token: 'sameUser',
                deps: ['user', '$transition$'],
                resolveFn: async (user: any, transition: any) => {
                    const params = transition.params();
                    return ForceSameUser('home.user.changeemail', user, params, Promise);
                }
            }
        ]
    },
    {
        name: 'home.user.changeemail.request',
        url: '',
        views: {
            'contentarea@home': {
                component: ChangeEmailRequestComponent
            }
        }
    },
    {
        name: 'home.user.changeemail.confirm',
        url: '/confirm?email&token',
        views: {
            'contentarea@home': {
                component: ChangeEmailConfirmComponent
            }
        }
    },
    {
        name: 'home.user.login',
        url: '/login?url',
        params: {
            url: null
        },
        resolve: [
            {
                // A `url` param is a redirect order, not a suggestion. By the time this
                // runs the parent `user` resolve has recovered the session from the
                // cookie, so a valid one here means there is nothing to log in to —
                // follow the order instead of asking for credentials we do not need.
                // Typical case: the tab was parked on the login screen while the session
                // was established elsewhere, then reloaded.
                token: 'sessionRedirect',
                deps: [Transition, UserService, StateHelperService, StateService, 'user'],
                resolveFn: (
                    transition: Transition,
                    userService: UserService,
                    stateHelperService: StateHelperService,
                    stateService: StateService) => {

                    const redirectUrl = transition.params()?.url ?? null;

                    // Only a `url` param triggers this. A logged-in user who asks for a
                    // bare /user/login still gets the form — that is how you sign in as
                    // somebody else.
                    if (!redirectUrl || !userService.session.hasUser()) return false;

                    // Abort before navigating: throwing a TargetState from a resolve is
                    // handled as a transition error here, not as a redirect.
                    transition.abort();

                    if (!stateHelperService.navigateByReturnUrl(redirectUrl)) {
                        // Unusable return URL (it pointed back at the login screen).
                        // Land somewhere real rather than strand the aborted transition.
                        const next: any = userService.options.getLogonDestinationState();
                        stateService.go(next.name, next.params);
                    }

                    return true;
                }
            },
            {
                token: 'authMethods',
                // Depends on sessionRedirect so the two cannot race: on an OIDC site this
                // resolve sets window.location to the provider, which would fight the SPA
                // navigation sessionRedirect just started.
                deps: [HttpClient, 'siteConfig', Window, 'sessionRedirect'],
                resolveFn: async (
                    http: HttpClient,
                    siteConfig: any,
                    window: Window,
                    sessionRedirect: boolean) => {

                    // Already redirecting somewhere useful — there is no form to prepare,
                    // and no reason to bounce an authenticated user to the OAuth provider.
                    if (sessionRedirect) return {};

                    try {
                        if (_.get(siteConfig, 'authenticationMode', 'db') == 'oidc') {
                            window.location.href = '/site/oauth';
                            return {};
                        }
                        const response = await doGetPromise(http, '/site/authmethods', {});
                        const result = (response as any)?.data || {};
                        return result;
                    } catch (error) {
                        console.warn('Could not fetch authentication methods:', error);
                        return {};
                    }
                }
            }
        ],
        views: {
            'contentarea@home': {
                component: LoginComponent
            }
        }
    },
    {
        name: 'home.user.lostpassword',
        url: '/lostpassword',
        views: {
            'contentarea@home': {
                component: LostPasswordViewComponent
            }
        }
    },
    {
        name: 'home.user.register',
        url: '/register?email&token&invite',
        params: {
            email: {
                value: '',
                squash: true
            },
            token: {
                value: '',
                squash: true
            },
            invite: {
                value: '',
                squash: true
            }
        },
        resolve: [
            {
                token: 'requested',
                deps: ['$transition$', UserService],
                resolveFn: async (transition: any, userService: UserService) => {
                    const params = transition.params();
                    if (params.email) {
                        try {
                            await userService.submitEmail(decodeURIComponent(params.email), 0, '', true);
                            return true;
                        } catch (error) {
                            return false;
                        }
                    }
                    return false;
                }
            },
            {
                token: 'confirmed',
                deps: ['requested', '$transition$', UserService],
                resolveFn: async (requested: boolean, transition: any, userService: UserService) => {
                    if (!requested) return false;

                    const params = transition.params();
                    if (params.token) {
                        try {
                            await userService.validateToken(decodeURIComponent(params.email), params.token);
                            return true;
                        } catch (error) {
                            return false;
                        }
                    }
                    return false;
                }
            },
            {
                token: 'msg',
                deps: ['requested', 'confirmed', UserService],
                resolveFn: async (requested: boolean, confirmed: boolean, userService: UserService) => {
                    return userService.getMsg();
                }
            },
            {
                token: 'registrationMode',
                deps: ['siteConfig'],
                resolveFn: async (siteConfig: any) => {
                    return _.get(siteConfig, 'registrationMode', 0);
                }
            }
        ],
        views: {
            'contentarea@home': {
                component: UserRegistrationRequestComponent
            }
        }
    },
    {
        name: 'home.user.reset',
        url: '/reset?unixName&confirmationHash',
        params: {
            unixName: null,
            confirmationHash: null
        },
        resolve: [
            {
                token: 'passwordRulesDesc',
                deps: ['siteConfig'],
                resolveFn: async (siteConfig: any) => {
                    return _.get(siteConfig, 'passwordRulesDesc', 0);
                }
            }
        ],
        views: {
            'contentarea@home': {
                component: ResetPasswordViewComponent
            }
        }
    },
    {
        name: 'home.user.regrequest',
        url: '/registration/request',
        resolve: [
            {
                token: 'registrationMode',
                deps: ['siteConfig'],
                resolveFn: async (siteConfig: any) => {
                    return _.get(siteConfig, 'registrationMode', 0);
                }
            }
        ],
        views: {
            'contentarea@home': {
                component: UserRegistrationRequestComponent
            }
        }
    },
    {
        name: 'home.user.userhome',
        url: '/:unixName',
        resolve: [
            {
                token: 'user',
                deps: ['$transition$', UserRest],
                resolveFn: async (transition: any, userRest: UserRest) => {
                    const params = transition.params();
                    return userRest.getFull(params.unixName);
                }
            },
            {
                token: 'tour',
                deps: [TourService],
                resolveFn: async (tourService: TourService) => {
                    return tourService.setTour('userHomeCtrl');
                }
            }
        ],
        views: {
            'contentarea@home': {
                component: UserHomeViewComponent
            }
        }
    },
    {
        name: 'home.user.my',
        url: '/:unixName/my?trackers',
        reloadOnSearch: false,
        params: {
            trackers: {
                value: '',
                type: 'string',
                squash: true
            }
        },
        resolve: [
            {
                token: 'sameUser',
                deps: ['$transition$', UserService],
                resolveFn: async (transition: any, userService: UserService) => {
                    const params = transition.params();
                    return ForceSameUser('home.user.my', userService.session.getUser(), params, Promise);
                }
            },
            {
                token: 'user',
                deps: ['$transition$', UserRest],
                resolveFn: async (transition: any, userRest: UserRest) => {
                    const params = transition.params();
                    return userRest.getMy(params.unixName);
                }
            },
            {
                token: 'tour',
                deps: [TourService],
                resolveFn: async (tourService: TourService) => {
                    return tourService.setTour('userMyCtrl');
                }
            }
        ],
        views: {
            'contentarea@home': {
                component: UserMyViewComponent
            }
        }
    },
    {
        name: 'home.user.notifications',
        url: '/:unixName/notifications?page_size&page_num&sort_field&sort_dir&user&itemSection&itemRefId&comment&project',
        reloadOnSearch: false,
        params: {
            page_num: {
                value: 1,
                type: 'int',
                squash: true
            },
            page_size: {
                value: 20,
                type: 'int',
                squash: true
            },
            sort_field: {
                value: 'postDate',
                squash: true
            },
            sort_dir: {
                value: 'desc',
                squash: true
            },
            user: {
                value: '',
                squash: true
            },
            itemSection: {
                value: '',
                squash: true
            },
            itemRefId: {
                value: '',
                squash: true
            },
            comment: {
                value: '',
                squash: true
            },
            project: {
                value: '',
                squash: true
            }
        },
        resolve: [
            {
                token: 'user',
                deps: ['$transition$', UserRest],
                resolveFn: async (transition: any, userRest: UserRest) => {
                    const params = transition.params();
                    return userRest.getMy(params.unixName);
                }
            }
        ],
        views: {
            'contentarea@home': {
                component: UserNotificationsViewComponent
            }
        }
    },
    {
        name: 'home.user.edit',
        url: '/:unixName/edit',
        resolve: [
            {
                token: 'tour',
                deps: [TourService],
                resolveFn: async (tourService: TourService) => {
                    return tourService.setTour('editProfileCtrl');
                }
            },
            {
                token: 'sameUser',
                deps: ['$transition$', UserService],
                resolveFn: async (transition: any, userService: UserService) => {
                    const params = transition.params();
                    return ForceSameUser('home.user.edit', userService.session.getUser(), params, Promise);
                }
            },
            {
                token: 'user',
                deps: ['$transition$', UserRest],
                resolveFn: async (transition: any, userRest: UserRest) => {
                    const params = transition.params();
                    return userRest.getFull(params.unixName);
                }
            }
        ],
        views: {
            'contentarea@home': {
                component: UserProfileEditComponent
            }
        }
    },
    {
        name: 'home.user.ssh',
        url: '/:unixName/ssh',
        resolve: [
            {
                token: 'sameUser',
                deps: ['$transition$', UserService],
                resolveFn: async (transition: any, userService: UserService) => {
                    const params = transition.params();
                    return ForceSameUser('home.user.ssh', userService.session.getUser(), params, Promise);
                }
            },
            {
                token: 'user',
                deps: ['$transition$', UserRest],
                resolveFn: async (transition: any, userRest: UserRest) => {
                    const params = transition.params();
                    return userRest.getFull(params.unixName);
                }
            }
        ],
        views: {
            'contentarea@home': {
                component: SshKeysViewComponent
            }
        }
    },
    {
        name: 'home.user.myreports',
        url: '/:unixName/reports?page_num&page_size&owner',
        reloadOnSearch: false,
        params: {
            page_num: {
                value: 1,
                type: 'int',
                dynamic: true,
                squash: true
            },
            page_size: {
                value: 20,
                type: 'int',
                dynamic: true,
                squash: true
            }
        },
        resolve: [
            {
                token: 'user',
                deps: [UserService],
                resolveFn: async (userService: UserService) => {
                    return userService.session.getUser();
                }
            },
            {
                token: 'sameUser',
                deps: ['user', '$transition$'],
                resolveFn: async (user: any, transition: any) => {
                    const params = transition.params();
                    return ForceSameUser('home.user.myreports', user, params, Promise);
                }
            },
            {
                token: 'tour',
                deps: [TourService],
                resolveFn: async (tourService: TourService) => {
                    return tourService.setTour('myreportsBrowseCtrl');
                }
            },
            {
                token: 'myReports',
                deps: ['$transition$', MyReportsRest, UiService],
                resolveFn: async (transition: any, myReportsRest: MyReportsRest, uiService: UiService) => {
                    const params = transition.params();
                    try {
                        const response = await myReportsRest.fetch(params);
                        return _.get(response, 'items', []);
                    } catch (error) {
                        console.log(error);
                        uiService.message.showSuccess("", "Could not fetch my reports");
                        return [];
                    }
                }
            }
        ],
        views: {
            'contentarea@home': {
                component: MyReportsListComponent
            }
        }
    },
    {
        name: 'home.user.myreports.report',
        url: '/:id',
        reloadOnSearch: false,
        params: {
            page_num: {
                value: 1,
                type: 'int',
                dynamic: true,
                squash: true
            },
            page_size: {
                value: 20,
                type: 'int',
                dynamic: true,
                squash: true
            }
        },
        resolve: [
            {
                token: 'tour',
                deps: [TourService],
                resolveFn: async (tourService: TourService) => {
                    return tourService.setTour('myreportsDetailsCtrl');
                }
            },
            {
                token: 'myReport',
                deps: ['$transition$', 'user', MyReportsRest],
                resolveFn: async (transition: any, user: any, myReportsRest: MyReportsRest) => {
                    const params = transition.params();
                    if (_.get(params, 'id', 0) == 0) {
                        return {
                            id: 0,
                            reportName: "New Report",
                            isShared: false,
                            reportType: 0,
                            user: user
                        };
                    } else {
                        return myReportsRest.getDetails(params.id);
                    }
                }
            }
        ],
        views: {
            'contentarea@home': {
                component: MyReportsReportComponent
            }
        }
    },
    {
        name: 'home.user.notebook',
        url: '/:unixName/notebook',
        resolve: [
            {
                token: 'user',
                deps: [UserService],
                resolveFn: async (userService: UserService) => {
                    return userService.session.getUser();
                }
            },
            {
                token: 'sameUser',
                deps: ['user', '$transition$'],
                resolveFn: async (user: any, transition: any) => {
                    const params = transition.params();
                    return ForceSameUser('home.user.notebook', user, params, Promise);
                }
            }
        ],
        views: {
            'contentarea@home': {
                component: UserNotebookComponent
            }
        }
    },
    {
        // Public, read-only view of a single notebook page reachable by a shareable link.
        // No same-user guard: the backend serves public pages to anyone and 403s otherwise.
        name: 'home.user.notebookpage',
        url: '/:unixName/notebook/:pagename',
        views: {
            'contentarea@home': {
                component: UserNotebookPageComponent
            }
        }
    }
];